/**
 * Reactive-variable store for the pipeline Review-queue drawer (SE-5499).
 *
 * Holds the drawer's open/closed state and an optional board scope. Apollo
 * `makeVar` matches the existing frontend-v2 store convention (mirrors
 * `features/contracts/stores/contractDrawerStore`), so any surface can open the
 * queue by calling the helper below without prop-drilling a callback.
 * @module features/player-kanban/stores/reviewQueueDrawerStore
 */

import { makeVar } from "@apollo/client";

/** Serializable state of the Review-queue drawer. */
export interface ReviewQueueDrawerState {
  /** Whether the drawer is open. */
  readonly open: boolean;
  /**
   * Optional board scope. When set, only that board's review requests are
   * fetched (the `kanbanBoardId` query arg); null shows the whole org queue.
   */
  readonly kanbanBoardId: string | null;
}

/** Initial (closed) state. */
const INITIAL_STATE: ReviewQueueDrawerState = {
  open: false,
  kanbanBoardId: null,
};

/**
 * Apollo reactive variable holding the Review-queue drawer state.
 * @remarks Subscribe via `useReactiveVar(reviewQueueDrawerVar)` (or the
 * `useReviewQueueDrawerState` hook) and update through the helper below.
 */
export const reviewQueueDrawerVar =
  makeVar<ReviewQueueDrawerState>(INITIAL_STATE);

/** Reset the drawer to its initial closed state. */
export function resetReviewQueueDrawer(): void {
  reviewQueueDrawerVar(INITIAL_STATE);
}
